import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import maplibregl from 'maplibre-gl';

// Configure MapLibre GL worker
if (typeof window !== 'undefined') {
  try {
    // Use the public URL path to load the worker from your server
    const workerUrl = `${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`;
    maplibregl.workerUrl = workerUrl;
  } catch (err) {
    // Non-fatal; map will still attempt default worker behavior
    console.warn('Failed to set maplibre workerUrl:', err);
  }
}

import {
  bbox as turfBbox,
  pointsWithinPolygon,
  circle as turfCircle,
  featureCollection as turfFeatureCollection,
  booleanIntersects,
} from '@turf/turf';
import '../../../assets/styles/MaptilerMap.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import config from '../../../config';
import {
  NEIGHBORHOODS,
  SCHOOL_DISTRICTS_DATA,
  SCHOOLS_DATA,
} from '../../../data';

const MAP_STYLE_URL = `https://api.maptiler.com/maps/dataviz-light/style.json?key=${config.MAPTILER_API_KEY}`;
const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };
const INITIAL_VIEWPORT = { center: [-96.8057, 32.7782], zoom: 4.5 };

// Utility function to generate random light colors
const generateLightColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 30) + 50; // 50-80%
  const lightness = Math.floor(Math.random() * 20) + 70; // 70-90%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Create a consistent color map for neighborhoods based on their name
const createNeighborhoodColorMap = (features) => {
  const colorMap = new Map();
  if (!features || !Array.isArray(features)) {
    return colorMap;
  }

  features.forEach((feature) => {
    const neighborhoodName =
      feature.properties?.title ||
      feature.properties?.name ||
      feature.properties?.Neighborhood ||
      '';
    if (neighborhoodName && !colorMap.has(neighborhoodName.toLowerCase())) {
      colorMap.set(neighborhoodName.toLowerCase(), generateLightColor());
    }
  });

  return colorMap;
};

// Add color property to neighborhoods
const addColorsToNeighborhoods = (collection, colorMap) => {
  if (!collection || !collection.features) {
    return collection;
  }

  return {
    ...collection,
    features: collection.features
      .filter((feature) => {
        // Validate geometry exists and is valid
        if (!feature.geometry || !feature.geometry.type || !feature.geometry.coordinates) {
          console.warn('[DEBUG] Neighborhood feature missing valid geometry:', feature);
          return false;
        }
        return true;
      })
      .map((feature) => {
        const neighborhoodName =
          feature.properties?.title ||
          feature.properties?.name ||
          feature.properties?.Neighborhood ||
          '';
        const color = colorMap.get(neighborhoodName.toLowerCase()) || '#2563eb';

        return {
          ...feature,
          properties: {
            ...feature.properties,
            __color: color,
            __name: neighborhoodName,
          },
        };
      }),
  };
};

// Create a consistent color map for school districts based on their name
const createDistrictColorMap = (features) => {
  const colorMap = new Map();
  if (!features || !Array.isArray(features)) {
    return colorMap;
  }

  features.forEach((feature) => {
    const districtName =
      feature.properties?.NAME ||
      feature.properties?.name ||
      feature.properties?.District ||
      '';
    if (districtName && !colorMap.has(districtName.toLowerCase())) {
      colorMap.set(districtName.toLowerCase(), generateLightColor());
    }
  });

  return colorMap;
};

// Add color property to districts
const addColorsToDistricts = (collection, colorMap) => {
  if (!collection || !collection.features) {
    console.log('[DEBUG] addColorsToDistricts: Invalid collection');
    return collection;
  }

  console.log('[DEBUG] addColorsToDistricts: Processing', collection.features.length, 'features with colorMap size', colorMap.size);

  const validatedFeatures = collection.features.map((feature, idx) => {
    try {
      const districtName =
        feature.properties?.NAME ||
        feature.properties?.name ||
        feature.properties?.District ||
        '';
      const color = colorMap.get(districtName.toLowerCase()) || '#06b6d4';

      // Validate feature has geometry
      if (!feature.geometry || !feature.geometry.type || !feature.geometry.coordinates) {
        console.warn('[DEBUG] Invalid geometry for feature', idx, districtName);
        return null;
      }

      if (idx === 0) {
        console.log('[DEBUG] First feature - name:', districtName, 'color:', color, 'geometry type:', feature.geometry.type);
      }

      const enrichedFeature = {
        ...feature,
        properties: {
          ...feature.properties,
          __color: color,
          __name: districtName,
        },
      };

      return enrichedFeature;
    } catch (e) {
      console.error('[DEBUG] Error processing feature', idx, e);
      return null;
    }
  }).filter(f => f !== null);

  console.log('[DEBUG] After validation:', validatedFeatures.length, 'valid features out of', collection.features.length);

  return {
    ...collection,
    features: validatedFeatures,
  };
};

const CITY_VIEWPORTS = {
  Austin: { center: [-97.7431, 30.2672], zoom: 11 },
  Chicago: { center: [-87.6298, 41.8781], zoom: 11 },
  Denver: { center: [-104.9903, 39.7392], zoom: 11 },
  Houston: { center: [-95.3698, 29.7604], zoom: 11 },
  'San Antonio': { center: [-98.4936, 29.4241], zoom: 11 },
  'Dallas/Fort Worth': { center: [-97.0409, 32.8998], zoom: 10 },
  DFW: { center: [-97.0409, 32.8998], zoom: 10 },
  'New York': { center: [-74.006, 40.7128], zoom: 11 },
  Nashville: { center: [-86.7816, 36.1627], zoom: 11 },
};

const MAP_SOURCES = {
  NEIGHBORHOODS: 'neighborhoods',
  NEIGHBORHOODS_SELECTED: 'neighborhoods-selected',
  NEIGHBORHOODS_HOVER: 'neighborhoods-hovered',
  APARTMENTS: 'apartments',
  SCHOOLS: 'schools',
  DISTRICTS: 'districts',
  CUSTOM_POLYGONS: 'custom-polygons',
  DRAW_CURRENT: 'draw-current',
  RADIUS: 'radius-circle',
};

const LAYER_IDS = {
  DISTRICTS_FILL: 'districts-fill',
  DISTRICTS_OUTLINE: 'districts-outline',
  DISTRICTS_TEXT: 'districts-text',
  NEIGHBORHOODS_FILL: 'neighborhoods-fill',
  NEIGHBORHOODS_OUTLINE: 'neighborhoods-outline',
  NEIGHBORHOODS_SELECTED_FILL: 'neighborhoods-selected-fill',
  NEIGHBORHOODS_SELECTED_OUTLINE: 'neighborhoods-selected-outline',
  NEIGHBORHOODS_HOVER_OUTLINE: 'neighborhoods-hover-outline',
  NEIGHBORHOODS_TEXT: 'neighborhoods-text',
  CUSTOM_POLYGONS_FILL: 'custom-polygons-fill',
  CUSTOM_POLYGONS_OUTLINE: 'custom-polygons-outline',
  DRAW_CURRENT_LINE: 'draw-current-line',
  DRAW_CURRENT_POINTS: 'draw-current-points',
  RADIUS_FILL: 'radius-fill',
  RADIUS_OUTLINE: 'radius-outline',
  APARTMENTS_CIRCLE: 'apartments-circle',
  SCHOOLS_CIRCLE: 'schools-circle',
};

const sanitizeAndAssignIds = (collection, prefix) => {
  if (!collection) {
    return EMPTY_COLLECTION;
  }
  // If collection is just an array/object, wrap/fix it? 
  // Expecting FeatureCollection or similar. 
  // If raw JSON from backend, it should be FeatureCollection.
  // We can just rely on backend result being FeatureCollection.

  if (collection.type === 'FeatureCollection' && Array.isArray(collection.features)) {
    // If IDs are already present and unique, we might not need this?
    // But MaptilerMap logic relies heavily on __id property.
    // Backend converter sets 'id' in properties.
    // Let's ensure we map 'id' to '__id' if missing.
    const features = collection.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...(feature.properties || {}),
        __id: feature.properties?.__id || String(feature.properties?.id || `${prefix}${index}`),
      },
    }));
    return {
      type: 'FeatureCollection',
      features,
    };
  }

  return EMPTY_COLLECTION;
};

const loadRemoteGeoJson = async (source, prefix) => {
  if (typeof source !== 'string' || !source) {
    throw new Error('Invalid GeoJSON source');
  }
  if (typeof window === 'undefined') {
    throw new Error('GeoJSON loading requires a browser environment');
  }

  const { origin, pathname } = window.location;
  const stripped = source.replace(/^\//, '');
  const pathPrefix = pathname.endsWith('/')
    ? pathname
    : pathname.substring(0, pathname.lastIndexOf('/') + 1);

  const candidates = [
    source,
    `${origin}${source.startsWith('/') ? source : `/${source}`}`,
    `${origin}${pathPrefix}${stripped}`,
  ];

  const attempted = new Set();
  let lastError = null;

  for (const candidate of candidates) {
    const url = candidate.startsWith('http')
      ? candidate
      : candidate;

    if (attempted.has(url)) {
      continue;
    }
    attempted.add(url);

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) {
        lastError = new Error(`HTTP ${response.status}`);
        continue;
      }
      const data = await response.json();
      return sanitizeAndAssignIds(data, prefix);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Failed to load GeoJSON');
};

const collectApartmentIdsInShapes = (apartmentsCollection, shapes) => {
  const ids = new Set();
  if (!apartmentsCollection || !apartmentsCollection.features?.length) {
    return ids;
  }

  shapes.forEach((shape) => {
    if (!shape) {
      return;
    }
    try {
      const polygonCollection =
        shape.type === 'FeatureCollection' ? shape : turfFeatureCollection([shape]);
      const inside = pointsWithinPolygon(apartmentsCollection, polygonCollection);
      inside?.features?.forEach((feature) => {
        const id = feature.properties?.__id;
        if (id) {
          ids.add(id);
        }
      });
    } catch (error) {
      console.warn('collectApartmentIdsInShapes error', error);
    }
  });

  return ids;
};

const countApartmentsInShapes = (apartmentsCollection, shapes) =>
  collectApartmentIdsInShapes(apartmentsCollection, shapes).size;

const MaptilerMap = ({
  className = '',
  cityName,
  availableNeighborhoods = [],
  selectedNeighborhoods = [],
  onToggleNeighborhood,
  isOpen = false,
  showNeighborhoods = true,
  showApartments = true,
  showSchools = false,
  showDistricts = false,
  onlySelectedTop = false,
  drawMode = false,
  radiusMode = false,
  radiusActive = false,
  radiusInMiles = 0.5,
  radiusCenter,
  onDrawUpdate,
  onRadiusUpdate,
  onStatsUpdate,
  clearDrawTrigger = 0,
  selectedZipCodes = [],
  apartmentsData = null,
  neighborhoodsData = null,
}) => {
  // Debug: ensure the configured API key is available at runtime
  // MAPTILER_API_KEY presence is intentionally not logged to avoid console noise in production.
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawHandlersRef = useRef(null);
  const drawStateRef = useRef({ isDrawing: false, points: [] });
  const radiusHandlersRef = useRef(null);

  const [isMapReady, setIsMapReady] = useState(false);
  const [hoveredNeighborhoodName, setHoveredNeighborhoodName] = useState(null);

  const [neighborhoodsGeoJson, setNeighborhoodsGeoJson] = useState(
    neighborhoodsData
      ? sanitizeAndAssignIds(neighborhoodsData, 'neighborhood-')
      : (typeof NEIGHBORHOODS === 'string'
        ? null
        : sanitizeAndAssignIds(NEIGHBORHOODS, 'neighborhood-'))
  );

  useEffect(() => {
    if (neighborhoodsData) {
      setNeighborhoodsGeoJson(sanitizeAndAssignIds(neighborhoodsData, 'neighborhood-'));
    }
  }, [neighborhoodsData]);

  // Initialize apartmentsGeoJson with passed data if available, or empty
  const [apartmentsGeoJson, setApartmentsGeoJson] = useState(
    apartmentsData ? sanitizeAndAssignIds(apartmentsData, 'apartment-') : EMPTY_COLLECTION
  );

  // Update apartmentsGeoJson when apartmentsData prop changes
  useEffect(() => {
    if (apartmentsData) {
      setApartmentsGeoJson(sanitizeAndAssignIds(apartmentsData, 'apartment-'));
    } else {
      setApartmentsGeoJson(EMPTY_COLLECTION);
    }
  }, [apartmentsData]);

  const [schoolsGeoJson, setSchoolsGeoJson] = useState(
    typeof SCHOOLS_DATA === 'string'
      ? null
      : sanitizeAndAssignIds(SCHOOLS_DATA, 'school-')
  );
  // Refs to hold the latest geojson objects so handlers defined once (eg. ensureCustomLayers)
  // can access up-to-date data across style reloads.
  const apartmentsGeoJsonRef = useRef(apartmentsGeoJson);
  const schoolsGeoJsonRef = useRef(schoolsGeoJson);
  const neighborhoodsGeoJsonRef = useRef(neighborhoodsGeoJson);
  const currentNeighborhoodsRef = useRef(null);

  useEffect(() => {
    apartmentsGeoJsonRef.current = apartmentsGeoJson;
  }, [apartmentsGeoJson]);

  useEffect(() => {
    schoolsGeoJsonRef.current = schoolsGeoJson;
  }, [schoolsGeoJson]);

  useEffect(() => {
    neighborhoodsGeoJsonRef.current = neighborhoodsGeoJson;
  }, [neighborhoodsGeoJson]);

  const [districtsGeoJson, setDistrictsGeoJson] = useState(
    typeof SCHOOL_DISTRICTS_DATA === 'string'
      ? null
      : sanitizeAndAssignIds(SCHOOL_DISTRICTS_DATA, 'district-')
  );

  // State for neighborhood colors
  const [neighborhoodColorMap, setNeighborhoodColorMap] = useState(new Map());
  const neighborhoodColorMapRef = useRef(new Map());

  // State for district colors
  const [districtColorMap, setDistrictColorMap] = useState(new Map());
  const districtColorMapRef = useRef(new Map());

  const drawnPolygonsRef = useRef([]);
  const [drawnVersion, setDrawnVersion] = useState(0);
  const drawHoverRef = useRef(null);
  // Base numeric id for the current drawing session so vertex feature ids
  // remain stable across repeated setData calls while drawing.
  const drawSessionBaseRef = useRef(0);

  // Ref to hold the latest radius polygon so handlers defined once can
  // access the up-to-date geometry (avoids stale-closure problems).
  const radiusPolygonRef = useRef(null);

  const availableNeighborhoodsRef = useRef(availableNeighborhoods);
  const toggleNeighborhoodRef = useRef(onToggleNeighborhood);
  const clearDrawTriggerRef = useRef(clearDrawTrigger);
  const drawModeRef = useRef(drawMode);
  const radiusModeRef = useRef(radiusMode);
  // refs for current visibility props so ensureCustomLayers can apply the correct visibility
  const showNeighborhoodsRef = useRef(showNeighborhoods);
  const showApartmentsRef = useRef(showApartments);
  const showSchoolsRef = useRef(showSchools);
  const showDistrictsRef = useRef(showDistricts);

  useEffect(() => {
    availableNeighborhoodsRef.current = availableNeighborhoods;
  }, [availableNeighborhoods]);


  useEffect(() => {
    showNeighborhoodsRef.current = showNeighborhoods;
  }, [showNeighborhoods]);

  useEffect(() => {
    showApartmentsRef.current = showApartments;
  }, [showApartments]);

  useEffect(() => {
    showSchoolsRef.current = showSchools;
  }, [showSchools]);

  useEffect(() => {
    showDistrictsRef.current = showDistricts;
  }, [showDistricts]);

  useEffect(() => {
    neighborhoodColorMapRef.current = neighborhoodColorMap;
  }, [neighborhoodColorMap]);

  useEffect(() => {
    toggleNeighborhoodRef.current = onToggleNeighborhood;
  }, [onToggleNeighborhood]);

  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  useEffect(() => {
    radiusModeRef.current = radiusMode;
  }, [radiusMode]);

  useEffect(() => {
    if (neighborhoodsData || neighborhoodsGeoJson !== null || typeof NEIGHBORHOODS !== 'string') {
      return;
    }
    let cancelled = false;
    loadRemoteGeoJson(NEIGHBORHOODS, 'neighborhood-')
      .then((data) => {
        if (!cancelled) {
          setNeighborhoodsGeoJson(data);
        }
      })
      .catch((error) => {
        console.error('Failed to load neighborhoods GeoJSON', error);
        if (!cancelled) {
          setNeighborhoodsGeoJson(EMPTY_COLLECTION);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [neighborhoodsGeoJson]);





  useEffect(() => {
    if (schoolsGeoJson !== null || typeof SCHOOLS_DATA !== 'string') {
      return;
    }
    let cancelled = false;
    loadRemoteGeoJson(SCHOOLS_DATA, 'school-')
      .then((data) => {
        if (!cancelled) {
          setSchoolsGeoJson(data);
        }
      })
      .catch((error) => {
        console.error('Failed to load schools GeoJSON', error);
        if (!cancelled) {
          setSchoolsGeoJson(EMPTY_COLLECTION);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [schoolsGeoJson]);

  useEffect(() => {
    if (districtsGeoJson !== null || typeof SCHOOL_DISTRICTS_DATA !== 'string') {
      return;
    }
    let cancelled = false;
    loadRemoteGeoJson(SCHOOL_DISTRICTS_DATA, 'district-')
      .then((data) => {
        if (!cancelled) {
          setDistrictsGeoJson(data);
        }
      })
      .catch((error) => {
        console.error('Failed to load districts GeoJSON', error);
        if (!cancelled) {
          setDistrictsGeoJson(EMPTY_COLLECTION);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [districtsGeoJson]);

  const currentNeighborhoods = useMemo(() => {
    if (!cityName) {
      return EMPTY_COLLECTION;
    }

    const lowerCity = cityName.toLowerCase();
    if (!neighborhoodsGeoJson?.features?.length) {
      return EMPTY_COLLECTION;
    }

    const filtered = neighborhoodsGeoJson.features.filter((feature) => {
      const featureCity =
        (feature.properties?.city ||
          feature.properties?.City ||
          feature.properties?.market ||
          '').toLowerCase();
      return featureCity === lowerCity;
    });

    const collection = {
      type: 'FeatureCollection',
      features: filtered,
    };

    // Create fresh color map for this city's neighborhoods if not already created
    let colorMap = neighborhoodColorMap;
    if (colorMap.size === 0 && filtered.length > 0) {
      colorMap = createNeighborhoodColorMap(filtered);
    }

    // Add colors to neighborhoods using the color map
    return addColorsToNeighborhoods(collection, colorMap);
  }, [cityName, neighborhoodsGeoJson, neighborhoodColorMap]);

  // Create color map based on all neighborhoods in the geojson
  useEffect(() => {
    if (neighborhoodsGeoJson?.features?.length) {
      const colorMap = createNeighborhoodColorMap(neighborhoodsGeoJson.features);
      setNeighborhoodColorMap(colorMap);
    }
  }, [neighborhoodsGeoJson]);

  // Create color map based on all districts in the geojson
  useEffect(() => {
    if (districtsGeoJson?.features?.length) {
      const colorMap = createDistrictColorMap(districtsGeoJson.features);
      setDistrictColorMap(colorMap);
    }
  }, [districtsGeoJson]);

  useEffect(() => {
    currentNeighborhoodsRef.current = currentNeighborhoods;
  }, [currentNeighborhoods]);

  const selectedNeighborhoodLabels = useMemo(
    () =>
      (selectedNeighborhoods || [])
        .map((option) => option?.label?.toLowerCase())
        .filter(Boolean),
    [selectedNeighborhoods]
  );

  const selectedNeighborhoodCollection = useMemo(() => {
    if (!selectedNeighborhoodLabels.length || !currentNeighborhoods.features?.length) {
      return EMPTY_COLLECTION;
    }

    const selectedSet = new Set(selectedNeighborhoodLabels);
    const selectedFeatures = currentNeighborhoods.features.filter((feature) => {
      const label = (
        feature.properties?.title ||
        feature.properties?.name ||
        feature.properties?.Neighborhood ||
        ''
      ).toLowerCase();
      return selectedSet.has(label);
    });

    return {
      type: 'FeatureCollection',
      features: selectedFeatures,
    };
  }, [currentNeighborhoods, selectedNeighborhoodLabels]);

  const neighborhoodOptionMap = useMemo(() => {
    const map = new Map();
    (availableNeighborhoods || []).forEach((option) => {
      if (option?.label) {
        map.set(option.label.toLowerCase(), option);
      }
    });
    return map;
  }, [availableNeighborhoods]);

  const neighborhoodOptionMapRef = useRef(neighborhoodOptionMap);
  useEffect(() => {
    neighborhoodOptionMapRef.current = neighborhoodOptionMap;
  }, [neighborhoodOptionMap]);

  // Apply colors to districts
  const currentDistricts = useMemo(() => {
    console.log('[DEBUG] currentDistricts: showDistricts =', showDistricts);

    // Don't process districts if they're not being shown
    if (!showDistricts) {
      console.log('[DEBUG] currentDistricts: Districts not enabled, returning EMPTY_COLLECTION');
      return EMPTY_COLLECTION;
    }

    if (!districtsGeoJson?.features?.length) {
      console.log('[DEBUG] currentDistricts: No features in districtsGeoJson');
      return EMPTY_COLLECTION;
    }

    console.log('[DEBUG] currentDistricts: Starting with', districtsGeoJson.features.length, 'features');

    // Create fresh color map for districts if not already created
    let colorMap = districtColorMap;
    if (colorMap.size === 0) {
      console.log('[DEBUG] currentDistricts: Creating new color map');
      colorMap = createDistrictColorMap(districtsGeoJson.features);
      console.log('[DEBUG] currentDistricts: Color map created with', colorMap.size, 'entries');
    }

    // Add colors to districts using the color map
    const result = addColorsToDistricts(districtsGeoJson, colorMap);
    console.log('[DEBUG] currentDistricts: Result has', result.features?.length, 'features');

    // Log first feature structure for debugging
    if (result.features?.length > 0) {
      console.log('[DEBUG] First district feature:', result.features[0]);
    }

    return result;
  }, [districtsGeoJson, districtColorMap, showDistricts]);

  const districtRef = useRef(null);
  useEffect(() => {
    districtRef.current = currentDistricts;
  }, [currentDistricts]);

  const radiusPolygonFeature = useMemo(() => {
    if (!radiusCenter) {
      return null;
    }
    try {
      return turfCircle(radiusCenter, radiusInMiles, {
        steps: 80,
        units: 'miles',
      });
    } catch (error) {
      console.warn('Failed to create radius circle', error);
      return null;
    }
  }, [radiusCenter, radiusInMiles]);

  // Keep a ref copy of the latest radius polygon so functions created
  // once (eg. ensureCustomLayers) can read its current value.
  useEffect(() => {
    radiusPolygonRef.current = radiusPolygonFeature;
  }, [radiusPolygonFeature]);

  const shapesForFiltering = useMemo(() => {
    const shapes = [];
    if (selectedNeighborhoodCollection.features.length) {
      shapes.push(selectedNeighborhoodCollection);
    }
    if (drawnPolygonsRef.current.length) {
      shapes.push(
        turfFeatureCollection([...drawnPolygonsRef.current])
      );
    }
    if (radiusActive && radiusPolygonFeature) {
      shapes.push(radiusPolygonFeature);
    }
    return shapes;
  }, [selectedNeighborhoodCollection, drawnVersion, radiusActive, radiusPolygonFeature]);

  const apartmentsInsideSet = useMemo(() => {
    if (!apartmentsGeoJson || !apartmentsGeoJson.features?.length) {
      return new Set();
    }
    if (!shapesForFiltering.length) {
      return new Set();
    }
    return collectApartmentIdsInShapes(apartmentsGeoJson, shapesForFiltering);
  }, [apartmentsGeoJson, shapesForFiltering]);

  // Compute the set of school IDs that fall within the active shapes. We
  // reuse the same collect function since it only relies on feature
  // geometry and the __id property.
  const schoolsInsideSet = useMemo(() => {
    if (!schoolsGeoJson || !schoolsGeoJson.features?.length) {
      return new Set();
    }
    if (!shapesForFiltering.length) {
      return new Set();
    }
    return collectApartmentIdsInShapes(schoolsGeoJson, shapesForFiltering);
  }, [schoolsGeoJson, shapesForFiltering]);

  // Helper to check if a point is close enough to the starting point to close the polygon
  const isNearFirstPoint = useCallback((point, firstPoint) => {
    if (!firstPoint) return false;
    const [x1, y1] = point;
    const [x2, y2] = firstPoint;
    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    return distance < 0.02; // Increased threshold for better hover visibility - ~500 meters at zoom level
  }, []);

  // Helper to show closing preview when near first point
  const updateDrawPreview = useCallback(
    (coordinates = []) => {
      if (!isMapReady || !mapRef.current) {
        return;
      }
      const source = mapRef.current.getSource(MAP_SOURCES.DRAW_CURRENT);
      if (!source) {
        return;
      }
      if (!coordinates.length) {
        // Clear any hover state when we remove preview features
        try {
          const prev = drawHoverRef.current;
          if (prev) {
            mapRef.current?.setFeatureState({ source: MAP_SOURCES.DRAW_CURRENT, id: prev }, { hover: false });
            drawHoverRef.current = null;
          }
        } catch (e) { }
        source.setData(EMPTY_COLLECTION);
        return;
      }

      // Check if we should show the polygon closing preview
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];
      const showClosingPreview = coordinates.length >= 3 &&
        isNearFirstPoint(lastPoint, firstPoint);

      let previewCoordinates = coordinates;
      if (showClosingPreview) {
        // If near the first point, snap to it for preview
        previewCoordinates = [...coordinates.slice(0, -1), firstPoint];
      }

      // Build features: the main geometry (LineString or Polygon) plus Point
      // features for each vertex so we can render small endpoint circles.
      const features = [];
      if (showClosingPreview) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [previewCoordinates] },
          properties: {},
        });
      } else {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: previewCoordinates },
          properties: {},
        });
      }

      // Add a Point feature for each coordinate so the points layer can render them
      // Determine how many points are permanent (user-clicked) vs ephemeral
      // preview points. Only permanent points should receive stable numeric
      // `id`s so they respond to feature-state hover. The ephemeral preview
      // point (the last mouse-follow point) will be added without an `id` so
      // it cannot be hovered/enlarged.
      const permanentCount = (drawStateRef.current && Array.isArray(drawStateRef.current.points))
        ? drawStateRef.current.points.length
        : 0;

      // Ensure we have a stable numeric base id for this draw session. If not
      // already set, create one so ids are stable across calls to setData.
      if (!drawSessionBaseRef.current) {
        drawSessionBaseRef.current = Number(String(Date.now()).slice(-6));
      }
      const base = drawSessionBaseRef.current;

      // Build point features only for already-clicked (permanent) vertices so
      // feature-state hover targets stable numeric ids. If there's a mouse-follow
      // preview point, add it as an ephemeral point WITHOUT an id — but skip
      // adding that ephemeral point when we're snapping-to-close so it doesn't
      // overlap the first permanent vertex and steal hover events.
      const permanentPoints = (drawStateRef.current && Array.isArray(drawStateRef.current.points))
        ? drawStateRef.current.points
        : [];

      permanentPoints.forEach((c, idx) => {
        const fid = base * 1000 + idx;
        features.push({
          type: 'Feature',
          id: fid,
          geometry: { type: 'Point', coordinates: c },
          properties: { __id: String(fid), vertexIndex: idx },
        });
      });

      // If the original coordinates array included an extra mouse-follow
      // coordinate (coordinates.length > permanentCount) and we're NOT showing
      // a closing preview (i.e. not snapping to first point), then add that
      // ephemeral preview point without an id so it cannot be hovered.
      if (Array.isArray(coordinates) && coordinates.length > permanentPoints.length && !showClosingPreview) {
        const last = coordinates[coordinates.length - 1];
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: last },
          properties: { vertexIndex: previewCoordinates.length - 1 },
        });
      }

      source.setData(turfFeatureCollection(features));

      // Update cursor style to indicate polygon can be closed
      if (mapRef.current) {
        mapRef.current.getCanvas().style.cursor = showClosingPreview ? 'pointer' : 'crosshair';
      }
    },
    [isMapReady, isNearFirstPoint]
  );

  const stopDrawInteraction = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (drawHandlersRef.current) {
      const { click, mousemove, dblclick } = drawHandlersRef.current;
      map.off('click', click);
      map.off('mousemove', mousemove);
      map.off('dblclick', dblclick);
      drawHandlersRef.current = null;
    }
    drawStateRef.current = { isDrawing: false, points: [] };
    updateDrawPreview();
    // reset session base id when drawing stops
    drawSessionBaseRef.current = 0;
    if (!radiusModeRef.current) {
      map.getCanvas().style.cursor = '';
    }
    map.doubleClickZoom.enable();
  }, [updateDrawPreview]);

  const finalizeDrawing = useCallback(
    () => {
      const map = mapRef.current;
      if (!map) {
        return;
      }
      const points = drawStateRef.current.points;
      if (!points || points.length < 3) {
        drawStateRef.current = { isDrawing: false, points: [] };
        updateDrawPreview();
        // reset session base id when drawing finalizes
        drawSessionBaseRef.current = 0;
        return;
      }

      // Create a valid polygon by ensuring first and last points match
      const closedPoints = [...points];
      if (JSON.stringify(closedPoints[0]) !== JSON.stringify(points[points.length - 1])) {
        closedPoints.push(points[0]);
      }

      const polygon = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [closedPoints],
        },
        properties: {
          __id: `draw-${Date.now()}`,
          timestamp: Date.now(),
        },
      };

      drawStateRef.current = { isDrawing: false, points: [] };
      updateDrawPreview();

      // Add the new polygon to existing ones
      drawnPolygonsRef.current = [...drawnPolygonsRef.current, polygon];
      const featureCollection = turfFeatureCollection([
        ...drawnPolygonsRef.current,
      ]);

      // Update the map with new polygon
      const source = map.getSource(MAP_SOURCES.CUSTOM_POLYGONS);
      source?.setData(featureCollection);

      // Trigger rerender
      setDrawnVersion((version) => version + 1);

      // Calculate apartments within all drawn polygons
      if (onDrawUpdate) {
        const apartmentsCount = countApartmentsInShapes(
          apartmentsGeoJson,
          [featureCollection]
        );
        onDrawUpdate({
          status: 'completed',
          polygons: featureCollection.features,
          apartmentsInPolygons: apartmentsCount,
        });
      }

      // Fly to the polygon bounds if it's the first one
      if (drawnPolygonsRef.current.length === 1) {
        try {
          const bounds = turfBbox(polygon);
          if (bounds && Number.isFinite(bounds[0])) {
            map.fitBounds(
              [
                [bounds[0], bounds[1]],
                [bounds[2], bounds[3]],
              ],
              {
                padding: { top: 48, bottom: 48, left: 48, right: 48 },
                maxZoom: 14,
              }
            );
          }
        } catch (error) {
          console.warn('Failed to fit bounds for drawn polygon', error);
        }
      }
    },
    [apartmentsGeoJson, onDrawUpdate, updateDrawPreview]
  );

  const startDrawInteraction = useCallback(() => {
    const map = mapRef.current;
    if (!map || drawHandlersRef.current) {
      return;
    }

    const handleClick = (event) => {
      const { lng, lat } = event.lngLat;
      const currentPoint = [lng, lat];
      const { points } = drawStateRef.current;

      // If we're starting a new polygon
      if (points.length === 0) {
        // initialize a stable numeric base id for this drawing session
        drawSessionBaseRef.current = Number(String(Date.now()).slice(-6));
        drawStateRef.current = { isDrawing: true, points: [currentPoint] };
        updateDrawPreview([currentPoint]);
        return;
      }

      // Check if we're closing the polygon.
      // Primary check: proximity to first point (existing behavior).
      let shouldClose = false;
      if (points.length >= 3 && isNearFirstPoint(currentPoint, points[0])) {
        shouldClose = true;
      }

      // Secondary check: if user clicked directly on the first permanent
      // vertex (enlarged on hover) then finalize regardless of proximity.
      if (!shouldClose && points.length >= 3 && mapRef.current) {
        try {
          const map = mapRef.current;
          // Query rendered features at click point for our draw points layer
          const hits = map.queryRenderedFeatures(event.point, { layers: [LAYER_IDS.DRAW_CURRENT_POINTS] }) || [];
          if (hits && hits.length) {
            // Determine expected id for the first permanent vertex
            const base = drawSessionBaseRef.current || Number(String(Date.now()).slice(-6));
            const firstId = base * 1000 + 0;
            for (let i = 0; i < hits.length; i++) {
              const h = hits[i];
              const hid = h.id !== undefined && h.id !== null ? h.id : h.properties?.__id;
              const vIdx = h.properties?.vertexIndex;
              if (vIdx === 0 || String(hid) === String(firstId) || String(hid) === String(base * 1000)) {
                shouldClose = true;
                break;
              }
            }
          }
        } catch (e) {
          // ignore query errors and fall back to proximity check
        }
      }

      if (shouldClose) {
        finalizeDrawing();
        return;
      }

      // Add the new point to the existing polygon
      const newPoints = [...points, currentPoint];
      drawStateRef.current = { isDrawing: true, points: newPoints };
      updateDrawPreview(newPoints);
    };

    const handleMouseMove = (event) => {
      if (!drawStateRef.current.isDrawing || drawStateRef.current.points.length === 0) {
        return;
      }
      const preview = [
        ...drawStateRef.current.points,
        [event.lngLat.lng, event.lngLat.lat],
      ];
      updateDrawPreview(preview);

      // Check if cursor is near the first point (closure point)
      if (drawStateRef.current.points.length >= 3 && mapRef.current) {
        const currentPoint = [event.lngLat.lng, event.lngLat.lat];
        const firstPoint = drawStateRef.current.points[0];
        const isNear = isNearFirstPoint(currentPoint, firstPoint);

        try {
          const map = mapRef.current;
          const base = drawSessionBaseRef.current || Number(String(Date.now()).slice(-6));
          const firstPointId = base * 1000 + 0;

          if (isNear) {
            // Apply hover state to the first point when cursor is nearby
            if (drawHoverRef.current !== firstPointId) {
              // Clear previous hover if different
              if (drawHoverRef.current !== null) {
                map.setFeatureState(
                  { source: MAP_SOURCES.DRAW_CURRENT, id: drawHoverRef.current },
                  { hover: false }
                );
              }
              // Apply new hover state
              map.setFeatureState(
                { source: MAP_SOURCES.DRAW_CURRENT, id: firstPointId },
                { hover: true }
              );
              drawHoverRef.current = firstPointId;
            }
          } else {
            // Remove hover state when cursor moves away
            if (drawHoverRef.current !== null) {
              map.setFeatureState(
                { source: MAP_SOURCES.DRAW_CURRENT, id: drawHoverRef.current },
                { hover: false }
              );
              drawHoverRef.current = null;
            }
          }
        } catch (e) {
          // Ignore errors in hover state management
        }
      }
    };

    const handleDoubleClick = (event) => {
      event.preventDefault();
      finalizeDrawing();
    };

    drawHandlersRef.current = {
      click: handleClick,
      mousemove: handleMouseMove,
      dblclick: handleDoubleClick,
    };

    map.doubleClickZoom.disable();
    map.getCanvas().style.cursor = 'crosshair';

    map.on('click', handleClick);
    map.on('mousemove', handleMouseMove);
    map.on('dblclick', handleDoubleClick);

    if (onDrawUpdate) {
      const polygonsCollection = turfFeatureCollection([
        ...drawnPolygonsRef.current,
      ]);
      onDrawUpdate({
        status: 'started',
        polygons: polygonsCollection.features,
        apartmentsInPolygons: countApartmentsInShapes(
          apartmentsGeoJson,
          polygonsCollection.features.length ? [polygonsCollection] : []
        ),
      });
    }
  }, [apartmentsGeoJson, finalizeDrawing, onDrawUpdate, updateDrawPreview]);

  const stopRadiusInteraction = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (radiusHandlersRef.current) {
      const { click } = radiusHandlersRef.current;
      map.off('click', click);
      radiusHandlersRef.current = null;
    }
    if (!drawModeRef.current) {
      map.getCanvas().style.cursor = '';
    }
  }, []);

  const startRadiusInteraction = useCallback(() => {
    const map = mapRef.current;
    if (!map || radiusHandlersRef.current) {
      return;
    }

    const handleClick = (event) => {
      const center = [event.lngLat.lng, event.lngLat.lat];
      const circle = turfCircle(center, radiusInMiles, {
        steps: 80,
        units: 'miles',
      });
      const count = countApartmentsInShapes(
        apartmentsGeoJson,
        circle ? [circle] : []
      );
      if (onRadiusUpdate) {
        onRadiusUpdate({
          status: 'center-chosen',
          center,
          radiusMiles: radiusInMiles,
          apartmentsInRadius: count,
          applied: false,
        });
      }
    };

    radiusHandlersRef.current = { click: handleClick };
    map.on('click', handleClick);
    map.getCanvas().style.cursor = 'crosshair';
  }, [apartmentsGeoJson, onRadiusUpdate, radiusInMiles]);

  const applyLayerVisibility = useCallback((layerIds, visible) => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const visibility = visible ? 'visible' : 'none';
    layerIds.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    });
  }, []);
  // Show only apartments and schools whose __id is present in idsSet when
  // there are active shapes. Clear the filter to show all markers when no
  // shapes are active. Uses a `match` expression on the `__id` property which
  // is assigned by `sanitizeAndAssignIds`.
  const setApartmentFilter = useCallback(
    (aptIdsSet, schoolIdsSet, hasActiveShapes, selectedZipCodesArg = []) => {
      const map = mapRef.current;
      if (!map) return;

      try {
        // Build zip filter if user selected ZIP codes
        const hasZipFilter = Array.isArray(selectedZipCodesArg) && selectedZipCodesArg.length > 0;
        const zipValues = hasZipFilter ? selectedZipCodesArg.map((z) => String(z).trim()) : [];
        // Keep ZIP values as strings (MapLibre match requires consistent types)
        const zipLiterals = Array.from(new Set(zipValues));
        // Try multiple common property names for ZIP in source data so filter
        // works regardless of casing or naming (zip, ZIP, zip_code, postalCode, etc.)
        const zipKeys = ['zip', 'ZIP', 'zipcode', 'zip_code', 'postalCode', 'postal_code', 'ZIPCODE'];
        let zipFilter = null;
        if (hasZipFilter) {
          const anyClauses = zipKeys.map((key) => ['match', ['get', key], zipLiterals, true, false]);
          // zipFilter succeeds if any of the property-key match expressions succeed
          zipFilter = ['any', ...anyClauses];
        }

        // Apartments: combine __id (shape) filter with zip filter when necessary
        if (!aptIdsSet || aptIdsSet.size === 0) {
          // If shapes are active but there are no matching apartments, hide all
          if (hasActiveShapes) {
            // When zip filter is present and shapes active, there are still no
            // apartments inside the shapes, so result should be empty.
            const emptyFilter = ['match', ['get', '__id'], [], true, false];
            if (map.getLayer(LAYER_IDS.APARTMENTS_CIRCLE)) {
              map.setFilter(LAYER_IDS.APARTMENTS_CIRCLE, emptyFilter);
            }
          } else {
            // No shapes active: apply zip filter if present, otherwise clear filter
            if (map.getLayer(LAYER_IDS.APARTMENTS_CIRCLE)) {
              if (zipFilter) {
                map.setFilter(LAYER_IDS.APARTMENTS_CIRCLE, zipFilter);
              } else {
                map.setFilter(LAYER_IDS.APARTMENTS_CIRCLE, null);
              }
            }
          }
        } else {
          const ids = Array.from(aptIdsSet);
          const idFilter = ['match', ['get', '__id'], ids, true, false];
          let finalFilter = idFilter;
          if (zipFilter) {
            finalFilter = ['all', idFilter, zipFilter];
          }
          if (map.getLayer(LAYER_IDS.APARTMENTS_CIRCLE)) {
            map.setFilter(LAYER_IDS.APARTMENTS_CIRCLE, finalFilter);
          }
        }

        // Schools: filter by schoolIdsSet only (ZIP filter not applied to schools)
        if (!schoolIdsSet || schoolIdsSet.size === 0) {
          if (hasActiveShapes) {
            // When shapes are active but no schools in them, hide all schools
            const emptyFilter = ['in', ['get', '__id'], ['literal', []]];
            if (map.getLayer(LAYER_IDS.SCHOOLS_CIRCLE)) {
              map.setFilter(LAYER_IDS.SCHOOLS_CIRCLE, emptyFilter);
            }
          } else {
            // No shapes active, show all schools
            if (map.getLayer(LAYER_IDS.SCHOOLS_CIRCLE)) {
              map.setFilter(LAYER_IDS.SCHOOLS_CIRCLE, null);
            }
          }
        } else {
          const sIds = Array.from(schoolIdsSet);
          // Use 'in' filter with literal array which is MapLibre GL compliant
          const sFilter = ['in', ['get', '__id'], ['literal', sIds]];
          if (map.getLayer(LAYER_IDS.SCHOOLS_CIRCLE)) {
            map.setFilter(LAYER_IDS.SCHOOLS_CIRCLE, sFilter);
          }
        }
      } catch (err) {
        // Non-fatal: log for diagnostics
        // eslint-disable-next-line no-console
        console.warn('Failed to apply apartment/school filter', err);
      }
    },
    []
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      console.log('[DEBUG] Map init skipped:', { containerExists: !!mapContainerRef.current, mapExists: !!mapRef.current });
      return;
    }

    console.log('[DEBUG] Initializing map with container:', mapContainerRef.current);
    const rect = mapContainerRef.current.getBoundingClientRect();
    console.log('[DEBUG] Container dimensions:', { width: rect.width, height: rect.height, visible: rect.height > 0 });

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: INITIAL_VIEWPORT.center,
      zoom: INITIAL_VIEWPORT.zoom,
    });

    mapRef.current = map;
    console.log('[DEBUG] Map instance created');

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

    map.on('load', () => {
      console.log('[DEBUG] Map load event fired');
      map.addSource(MAP_SOURCES.NEIGHBORHOODS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.NEIGHBORHOODS_SELECTED, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.NEIGHBORHOODS_HOVER, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.DISTRICTS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.CUSTOM_POLYGONS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.DRAW_CURRENT, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.RADIUS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.APARTMENTS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
      map.addSource(MAP_SOURCES.SCHOOLS, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });

      // If the remote style is reloaded/changed we may lose custom layers.
      // Ensure our custom sources and layers are present after any style change.
      const ensureCustomLayers = () => {
        const map = mapRef.current;
        if (!map) return;

        // Ensure our custom sources exist and are populated with the latest data.
        try {
          if (!map.getSource(MAP_SOURCES.APARTMENTS)) {
            map.addSource(MAP_SOURCES.APARTMENTS, {
              type: 'geojson',
              data: apartmentsGeoJsonRef.current || EMPTY_COLLECTION,
            });
            console.log('[DEBUG] APARTMENTS source added with', apartmentsGeoJsonRef.current?.features?.length || 0, 'features');
            // source re-added
          } else {
            // reset data to latest if source exists
            try {
              console.log('[DEBUG] Updating APARTMENTS source data with', apartmentsGeoJsonRef.current?.features?.length || 0, 'features');
              map.getSource(MAP_SOURCES.APARTMENTS).setData(
                apartmentsGeoJsonRef.current || EMPTY_COLLECTION
              );
            } catch (e) {
              console.error('[DEBUG] Error updating APARTMENTS source:', e);
              // ignore if source is not ready to accept data at this moment
            }
          }

          if (!map.getSource(MAP_SOURCES.SCHOOLS)) {
            map.addSource(MAP_SOURCES.SCHOOLS, {
              type: 'geojson',
              data: schoolsGeoJsonRef.current || EMPTY_COLLECTION,
            });
            console.log('[DEBUG] SCHOOLS source added with', schoolsGeoJsonRef.current?.features?.length || 0, 'features');
            // source re-added
          } else {
            try {
              console.log('[DEBUG] Updating SCHOOLS source data with', schoolsGeoJsonRef.current?.features?.length || 0, 'features');
              map.getSource(MAP_SOURCES.SCHOOLS).setData(
                schoolsGeoJsonRef.current || EMPTY_COLLECTION
              );
            } catch (e) {
              console.error('[DEBUG] Error updating SCHOOLS source:', e);
            }
          }

          if (!map.getSource(MAP_SOURCES.NEIGHBORHOODS)) {
            map.addSource(MAP_SOURCES.NEIGHBORHOODS, {
              type: 'geojson',
              data: currentNeighborhoodsRef.current || EMPTY_COLLECTION,
            });
            console.log('[DEBUG] NEIGHBORHOODS source added with', currentNeighborhoodsRef.current?.features?.length || 0, 'features');
            // source re-added
          } else {
            try {
              console.log('[DEBUG] Updating NEIGHBORHOODS source data with', currentNeighborhoodsRef.current?.features?.length || 0, 'features');
              map.getSource(MAP_SOURCES.NEIGHBORHOODS).setData(
                currentNeighborhoodsRef.current || EMPTY_COLLECTION
              );
            } catch (e) {
              console.error('[DEBUG] Error updating NEIGHBORHOODS source:', e);
            }
          }
          // Ensure districts source exists
          if (!map.getSource(MAP_SOURCES.DISTRICTS)) {
            try {
              console.log('[DEBUG] Adding DISTRICTS source with', districtRef.current?.features?.length || 0, 'features');
              map.addSource(MAP_SOURCES.DISTRICTS, {
                type: 'geojson',
                data: districtRef.current || EMPTY_COLLECTION,
              });
            } catch (e) {
              console.error('[DEBUG] Error adding DISTRICTS source:', e);
            }
          } else {
            try {
              console.log('[DEBUG] Setting DISTRICTS source data with', districtRef.current?.features?.length || 0, 'features');
              map.getSource(MAP_SOURCES.DISTRICTS).setData(districtRef.current || EMPTY_COLLECTION);
            } catch (e) {
              console.error('[DEBUG] Error setting DISTRICTS source data:', e);
            }
          }

          // Ensure custom polygons/draw/radius sources exist
          if (!map.getSource(MAP_SOURCES.CUSTOM_POLYGONS)) {
            try {
              map.addSource(MAP_SOURCES.CUSTOM_POLYGONS, {
                type: 'geojson',
                data: turfFeatureCollection([...drawnPolygonsRef.current] || []),
              });
            } catch (e) { }
          } else {
            try {
              map.getSource(MAP_SOURCES.CUSTOM_POLYGONS).setData(
                turfFeatureCollection([...drawnPolygonsRef.current])
              );
            } catch (e) { }
          }

          if (!map.getSource(MAP_SOURCES.DRAW_CURRENT)) {
            try {
              map.addSource(MAP_SOURCES.DRAW_CURRENT, {
                type: 'geojson',
                data: EMPTY_COLLECTION,
              });
            } catch (e) { }
          } else {
            try {
              map.getSource(MAP_SOURCES.DRAW_CURRENT).setData(EMPTY_COLLECTION);
            } catch (e) { }
          }

          if (!map.getSource(MAP_SOURCES.RADIUS)) {
            try {
              map.addSource(MAP_SOURCES.RADIUS, {
                type: 'geojson',
                data: radiusPolygonRef.current ? turfFeatureCollection([radiusPolygonRef.current]) : EMPTY_COLLECTION,
              });
            } catch (e) { }
          } else {
            try {
              map.getSource(MAP_SOURCES.RADIUS).setData(
                radiusPolygonRef.current ? turfFeatureCollection([radiusPolygonRef.current]) : EMPTY_COLLECTION
              );
            } catch (e) { }
          }
        } catch (err) {
          console.warn('[MaptilerMap] ensureCustomLayers: failed to re-add sources', err);
        }

        const safeAddLayer = (layerDef) => {
          try {
            if (!map.getLayer(layerDef.id)) {
              // Find the first symbol/label layer so we can insert our layer below labels
              const style = map.getStyle && map.getStyle();
              let beforeId = undefined;
              if (style?.layers && Array.isArray(style.layers)) {
                const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
                if (sym) beforeId = sym.id;
              }
              if (beforeId) {
                map.addLayer(layerDef, beforeId);
              } else {
                map.addLayer(layerDef);
              }
              // layer re-added
              // Ensure the freshly added layer respects the current visibility toggles
              try {
                const id = layerDef.id;
                let visible = true;
                if (id === LAYER_IDS.APARTMENTS_CIRCLE) visible = !!showApartmentsRef.current;
                else if (id === LAYER_IDS.SCHOOLS_CIRCLE) visible = !!showSchoolsRef.current;
                else if (
                  id === LAYER_IDS.NEIGHBORHOODS_FILL ||
                  id === LAYER_IDS.NEIGHBORHOODS_OUTLINE ||
                  id === LAYER_IDS.NEIGHBORHOODS_SELECTED_FILL ||
                  id === LAYER_IDS.NEIGHBORHOODS_SELECTED_OUTLINE ||
                  id === LAYER_IDS.NEIGHBORHOODS_HOVER_OUTLINE
                ) {
                  visible = !!showNeighborhoodsRef.current;
                } else if (
                  id === LAYER_IDS.DISTRICTS_FILL ||
                  id === LAYER_IDS.DISTRICTS_OUTLINE
                ) {
                  visible = !!showDistrictsRef.current;
                } else if (
                  id === LAYER_IDS.RADIUS_FILL ||
                  id === LAYER_IDS.RADIUS_OUTLINE
                ) {
                  visible = !!radiusPolygonRef.current;
                }
                map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
              } catch (e) {
                // ignore visibility set failures
              }
            }
          } catch (err) {
            console.warn('[MaptilerMap] failed to re-add layer', layerDef.id, err);
          }
        };

        try {
          // Re-create a subset of our custom layers if missing (non-destructive)
          // TEMPORARILY: Only add circle layers to isolate crash issue
          safeAddLayer({
            id: LAYER_IDS.APARTMENTS_CIRCLE,
            type: 'circle',
            source: MAP_SOURCES.APARTMENTS,
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3, 11, 6, 14, 10],
              'circle-color': '#ef4444',
              'circle-opacity': 0.8,
              'circle-stroke-width': 0.8,
              'circle-stroke-color': '#ffffff',
            },
          });

          safeAddLayer({
            id: LAYER_IDS.SCHOOLS_CIRCLE,
            type: 'circle',
            source: MAP_SOURCES.SCHOOLS,
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 12, 5, 15, 8],
              'circle-color': '#22c55e',
              'circle-opacity': 0.75,
              'circle-stroke-width': 0.6,
              'circle-stroke-color': '#ffffff',
            },
          });

          // DISABLED: All polygon/text layers commented out to debug crash
          // District layers (fill + outline)
          // safeAddLayer({
          //   id: LAYER_IDS.DISTRICTS_FILL,
          //   type: 'fill',
          //   source: MAP_SOURCES.DISTRICTS,
          //   filter: ['has', '__color'],
          //   paint: {
          //     'fill-color': ['coalesce', ['get', '__color'], '#06b6d4'],
          //     'fill-opacity': 0.12,
          //   },
          // });

          // safeAddLayer({
          //   id: LAYER_IDS.DISTRICTS_OUTLINE,
          //   type: 'line',
          //   source: MAP_SOURCES.DISTRICTS,
          //   filter: ['has', '__color'],
          //   paint: {
          //     'line-color': ['coalesce', ['get', '__color'], '#06b6d4'],
          //     'line-width': 1.2,
          //     'line-opacity': 0.9,
          //   },
          // });

          // District text labels - displays district names
          // safeAddLayer({
          //   id: LAYER_IDS.DISTRICTS_TEXT,
          //   type: 'symbol',
          //   source: MAP_SOURCES.DISTRICTS,
          //   layout: {
          //     'text-field': ['coalesce', ['get', '__name'], ''],
          //     'text-size': 11,
          //     'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          //     'text-offset': [0, 0],
          //     'text-anchor': 'center',
          //     'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
          //     'text-radial-offset': 1,
          //     'text-allow-overlap': false,
          //     'text-ignore-placement': false,
          //     'visibility': 'none',
          //   },
          //   paint: {
          //     'text-color': '#1f2937',
          //     'text-opacity': 0.7,
          //     'text-halo-color': '#ffffff',
          //     'text-halo-width': 1.5,
          //   },
          // });

          // Neighborhoods layers (ensureCustomLayers fallback)
          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_FILL,
            type: 'fill',
            source: MAP_SOURCES.NEIGHBORHOODS,
            paint: {
              'fill-color': ['coalesce', ['get', '__color'], '#2563eb'],
              'fill-opacity': 0.15,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.NEIGHBORHOODS,
            paint: {
              'line-color': ['coalesce', ['get', '__color'], '#2563eb'],
              'line-width': 1.5,
              'line-opacity': 0.8,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_SELECTED_FILL,
            type: 'fill',
            source: MAP_SOURCES.NEIGHBORHOODS_SELECTED,
            paint: {
              'fill-color': ['coalesce', ['get', 'fill'], '#2563eb'],
              'fill-opacity': 0.35,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_SELECTED_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.NEIGHBORHOODS_SELECTED,
            paint: {
              'line-color': ['coalesce', ['get', 'fill'], '#2563eb'],
              'line-width': 2.5,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_HOVER_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.NEIGHBORHOODS_HOVER,
            paint: {
              'line-color': '#111827',
              'line-width': 2.5,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.NEIGHBORHOODS_TEXT,
            type: 'symbol',
            source: MAP_SOURCES.NEIGHBORHOODS,
            layout: {
              'text-field': ['coalesce', ['get', '__name'], ''],
              'text-size': 14,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'visibility': 'visible',
            },
            paint: {
              'text-color': '#1f2937',
              'text-opacity': 0.85,
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            },
          });

          // District layers (fill + outline)
          safeAddLayer({
            id: LAYER_IDS.DISTRICTS_FILL,
            type: 'fill',
            source: MAP_SOURCES.DISTRICTS,
            filter: ['has', '__color'],
            paint: {
              'fill-color': ['coalesce', ['get', '__color'], '#06b6d4'],
              'fill-opacity': 0.12,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.DISTRICTS_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.DISTRICTS,
            filter: ['has', '__color'],
            paint: {
              'line-color': ['coalesce', ['get', '__color'], '#06b6d4'],
              'line-width': 1.2,
              'line-opacity': 0.9,
            },
          });

          // District text labels - displays district names
          safeAddLayer({
            id: LAYER_IDS.DISTRICTS_TEXT,
            type: 'symbol',
            source: MAP_SOURCES.DISTRICTS,
            layout: {
              'text-field': ['coalesce', ['get', '__name'], ''],
              'text-size': 11,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
              'text-radial-offset': 1,
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              'visibility': 'none',
            },
            paint: {
              'text-color': '#1f2937',
              'text-opacity': 0.7,
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.5,
            },
          });

          // Custom drawn polygons (fill + outline)
          safeAddLayer({
            id: LAYER_IDS.CUSTOM_POLYGONS_FILL,
            type: 'fill',
            source: MAP_SOURCES.CUSTOM_POLYGONS,
            paint: {
              'fill-color': '#f97316',
              'fill-opacity': 0.15,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.CUSTOM_POLYGONS_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.CUSTOM_POLYGONS,
            paint: {
              'line-color': '#c2410c',
              'line-width': 2,
            },
          });

          // Draw preview line (live drawing)
          safeAddLayer({
            id: LAYER_IDS.DRAW_CURRENT_LINE,
            type: 'line',
            source: MAP_SOURCES.DRAW_CURRENT,
            paint: {
              'line-color': '#6b21a8',
              'line-width': 2.5,
            },
          });

          // Draw preview points (endpoints / vertices) — support hover state
          safeAddLayer({
            id: LAYER_IDS.DRAW_CURRENT_POINTS,
            type: 'circle',
            source: MAP_SOURCES.DRAW_CURRENT,
            paint: {
              // enlarge on hover via feature-state `hover`
              'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 14, 6],
              'circle-radius-transition': { duration: 150, delay: 0 },
              'circle-color': '#6b21a8',
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
            },
            filter: ['==', ['geometry-type'], 'Point'],
          });

          // Radius visualization
          safeAddLayer({
            id: LAYER_IDS.RADIUS_FILL,
            type: 'fill',
            source: MAP_SOURCES.RADIUS,
            paint: {
              'fill-color': '#7c3aed',
              'fill-opacity': 0.15,
            },
          });

          safeAddLayer({
            id: LAYER_IDS.RADIUS_OUTLINE,
            type: 'line',
            source: MAP_SOURCES.RADIUS,
            paint: {
              'line-color': '#6d28d9',
              'line-width': 1.5,
            },
          });

          // No development-only debug layers here; keep only production layers.
        } catch (err) {
          console.warn('[MaptilerMap] ensureCustomLayers error', err);
        }
      };

      map.on('style.load', ensureCustomLayers);
      map.on('styledata', ensureCustomLayers);
      // Also ensure after the style becomes idle (tiles rendered)
      map.once('idle', ensureCustomLayers);

      // (debug logging removed to reduce console noise)

      // Add neighborhoods layers (fill, outline, selected, hover)
      try {
        // base fill
        map.addLayer({
          id: LAYER_IDS.NEIGHBORHOODS_FILL,
          type: 'fill',
          source: MAP_SOURCES.NEIGHBORHOODS,
          paint: {
            'fill-color': '#2563eb',
            'fill-opacity': 0.08,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.NEIGHBORHOODS_FILL,
            'visibility',
            showNeighborhoodsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        // base outline
        map.addLayer({
          id: LAYER_IDS.NEIGHBORHOODS_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.NEIGHBORHOODS,
          paint: {
            'line-color': '#2563eb',
            'line-width': 1.0,
            'line-opacity': 0.9,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.NEIGHBORHOODS_OUTLINE,
            'visibility',
            showNeighborhoodsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        // selected neighborhoods (fill)
        map.addLayer({
          id: LAYER_IDS.NEIGHBORHOODS_SELECTED_FILL,
          type: 'fill',
          source: MAP_SOURCES.NEIGHBORHOODS_SELECTED,
          paint: {
            'fill-color': ['coalesce', ['get', 'fill'], '#2563eb'],
            'fill-opacity': 0.35,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.NEIGHBORHOODS_SELECTED_FILL,
            'visibility',
            showNeighborhoodsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        // selected neighborhoods outline
        map.addLayer({
          id: LAYER_IDS.NEIGHBORHOODS_SELECTED_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.NEIGHBORHOODS_SELECTED,
          paint: {
            'line-color': ['coalesce', ['get', 'fill'], '#2563eb'],
            'line-width': 2.5,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.NEIGHBORHOODS_SELECTED_OUTLINE,
            'visibility',
            showNeighborhoodsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        // hover outline (single feature)
        map.addLayer({
          id: LAYER_IDS.NEIGHBORHOODS_HOVER_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.NEIGHBORHOODS_HOVER,
          paint: {
            'line-color': '#111827',
            'line-width': 2.5,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.NEIGHBORHOODS_HOVER_OUTLINE,
            'visibility',
            showNeighborhoodsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }
      } catch (err) {
        // ignore if layers already exist or cannot be added yet
      }

      // Add custom polygons / draw / radius layers so drawn shapes are visible
      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.CUSTOM_POLYGONS_FILL,
          type: 'fill',
          source: MAP_SOURCES.CUSTOM_POLYGONS,
          paint: {
            'fill-color': '#f97316',
            'fill-opacity': 0.15,
          },
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }
      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.CUSTOM_POLYGONS_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.CUSTOM_POLYGONS,
          paint: {
            'line-color': '#c2410c',
            'line-width': 2,
          },
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }

      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.DRAW_CURRENT_LINE,
          type: 'line',
          source: MAP_SOURCES.DRAW_CURRENT,
          paint: {
            'line-color': '#6b21a8',
            'line-width': 2.5,
          },
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }

      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.DRAW_CURRENT_POINTS,
          type: 'circle',
          source: MAP_SOURCES.DRAW_CURRENT,
          paint: {
            'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 14, 6],
            'circle-radius-transition': { duration: 150, delay: 0 },
            'circle-color': '#6b21a8',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
          },
          filter: ['==', ['geometry-type'], 'Point'],
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }

      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.RADIUS_FILL,
          type: 'fill',
          source: MAP_SOURCES.RADIUS,
          paint: {
            'fill-color': '#7c3aed',
            'fill-opacity': 0.15,
          },
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }

      try {
        const style = map.getStyle && map.getStyle();
        let beforeId = undefined;
        if (style?.layers && Array.isArray(style.layers)) {
          const sym = style.layers.find((l) => l.type === 'symbol' || /label/i.test(l.id));
          if (sym) beforeId = sym.id;
        }
        const layerDef = {
          id: LAYER_IDS.RADIUS_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.RADIUS,
          paint: {
            'line-color': '#6d28d9',
            'line-width': 1.5,
          },
        };
        if (beforeId) map.addLayer(layerDef, beforeId);
        else map.addLayer(layerDef);
      } catch (e) { }

      // Add apartments layer (production)
      try {
        map.addLayer({
          id: LAYER_IDS.APARTMENTS_CIRCLE,
          type: 'circle',
          source: MAP_SOURCES.APARTMENTS,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 11, 8, 14, 12],
            'circle-color': '#ef4444',
            'circle-opacity': 0.8,
            'circle-stroke-width': 0.8,
            'circle-stroke-color': '#ffffff',
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.APARTMENTS_CIRCLE,
            'visibility',
            showApartmentsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }
      } catch (err) {
        // ignore if layer already exists or cannot be added yet
      }

      // Add school districts layers (fill + outline)
      try {
        map.addLayer({
          id: LAYER_IDS.DISTRICTS_FILL,
          type: 'fill',
          source: MAP_SOURCES.DISTRICTS,
          paint: {
            'fill-color': ['coalesce', ['get', '__color'], '#06b6d4'],
            'fill-opacity': 0.12,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.DISTRICTS_FILL,
            'visibility',
            showDistrictsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        map.addLayer({
          id: LAYER_IDS.DISTRICTS_OUTLINE,
          type: 'line',
          source: MAP_SOURCES.DISTRICTS,
          filter: ['has', '__color'],
          paint: {
            'line-color': ['coalesce', ['get', '__color'], '#06b6d4'],
            'line-width': 1.2,
            'line-opacity': 0.9,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.DISTRICTS_OUTLINE,
            'visibility',
            showDistrictsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }

        // District text labels - displays district names
        map.addLayer({
          id: LAYER_IDS.DISTRICTS_TEXT,
          type: 'symbol',
          source: MAP_SOURCES.DISTRICTS,
          layout: {
            'text-field': ['coalesce', ['get', '__name'], ''],
            'text-size': 11,
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0],
            'text-anchor': 'center',
            'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
            'text-radial-offset': 1,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'visibility': 'none',
          },
          paint: {
            'text-color': '#1f2937',
            'text-opacity': 0.7,
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
        });
        try {
          map.setLayoutProperty(
            LAYER_IDS.DISTRICTS_TEXT,
            'visibility',
            showDistrictsRef.current ? 'visible' : 'none'
          );
        } catch (e) { }
      } catch (err) {
        // ignore if layer already exists or cannot be added yet
      }

      map.addLayer({
        id: LAYER_IDS.SCHOOLS_CIRCLE,
        type: 'circle',
        source: MAP_SOURCES.SCHOOLS,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8,
            4,
            11,
            8,
            14,
            12,
          ],
          'circle-color': '#22c55e',
          'circle-opacity': 0.75,
          'circle-stroke-width': 0.6,
          'circle-stroke-color': '#ffffff',
        },
      });
      try {
        map.setLayoutProperty(
          LAYER_IDS.SCHOOLS_CIRCLE,
          'visibility',
          showSchoolsRef.current ? 'visible' : 'none'
        );
      } catch (e) { }

      // production: rely on our apartment and school layers only (no dev debug layers)

      map.on('mousemove', LAYER_IDS.NEIGHBORHOODS_FILL, (event) => {
        if (drawModeRef.current || radiusModeRef.current) {
          return;
        }
        const feature = event.features && event.features[0];
        if (!feature) {
          return;
        }
        const hoverSource = map.getSource(MAP_SOURCES.NEIGHBORHOODS_HOVER);
        hoverSource?.setData(turfFeatureCollection([feature]));
        const neighborhoodName = feature.properties?.__name || '';
        setHoveredNeighborhoodName(neighborhoodName);
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', LAYER_IDS.NEIGHBORHOODS_FILL, () => {
        const hoverSource = map.getSource(MAP_SOURCES.NEIGHBORHOODS_HOVER);
        hoverSource?.setData(EMPTY_COLLECTION);
        setHoveredNeighborhoodName(null);
        if (drawModeRef.current || radiusModeRef.current) {
          map.getCanvas().style.cursor = 'crosshair';
        } else {
          map.getCanvas().style.cursor = '';
        }
      });

      map.on('click', LAYER_IDS.NEIGHBORHOODS_FILL, (event) => {
        if (drawModeRef.current || radiusModeRef.current) {
          return;
        }
        const feature = event.features && event.features[0];
        if (!feature) {
          return;
        }
        const label = (
          feature.properties?.title ||
          feature.properties?.name ||
          feature.properties?.Neighborhood ||
          ''
        ).toLowerCase();
        const option = neighborhoodOptionMapRef.current.get(label);
        if (option && toggleNeighborhoodRef.current) {
          toggleNeighborhoodRef.current(option);
        }
      });

      // Add popup on apartment click
      map.on('click', LAYER_IDS.APARTMENTS_CIRCLE, (e) => {
        if (drawModeRef.current || radiusModeRef.current) {
          return;
        }

        const feature = e.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const { name } = feature.properties;

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        new maplibregl.Popup()
          .setLngLat(coordinates)
          .setHTML(`<strong>${name}</strong>`)
          .addTo(map);
      });

      // Change cursor on hover
      map.on('mouseenter', LAYER_IDS.APARTMENTS_CIRCLE, () => {
        if (!drawModeRef.current && !radiusModeRef.current) {
          map.getCanvas().style.cursor = 'pointer';
        }
      });

      map.on('mouseleave', LAYER_IDS.APARTMENTS_CIRCLE, () => {
        if (!drawModeRef.current && !radiusModeRef.current) {
          map.getCanvas().style.cursor = '';
        }
      });

      // Add popup on school click
      map.on('click', LAYER_IDS.SCHOOLS_CIRCLE, (e) => {
        if (drawModeRef.current || radiusModeRef.current) {
          return;
        }

        const feature = e.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const { Full_Address, Street_Address, Principal_Name, Grade_Range, School_Type } = feature.properties;

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        const popupContent = `
          <div style="font-family: sans-serif; font-size: 12px; min-width: 200px;">
            <table style="border-collapse: collapse; width: 100%;">
              <tbody>
                ${School_Type ? `<tr><td style="font-weight: bold; padding: 4px; border-bottom: 1px solid #eee; width: 40%; vertical-align: top;">Type:</td><td style="padding: 4px; border-bottom: 1px solid #eee;">${School_Type}</td></tr>` : ''}
                ${Principal_Name ? `<tr><td style="font-weight: bold; padding: 4px; border-bottom: 1px solid #eee; vertical-align: top;">Principal:</td><td style="padding: 4px; border-bottom: 1px solid #eee;">${Principal_Name}</td></tr>` : ''}
                ${Grade_Range ? `<tr><td style="font-weight: bold; padding: 4px; border-bottom: 1px solid #eee; vertical-align: top;">Grades:</td><td style="padding: 4px; border-bottom: 1px solid #eee;">${Grade_Range.replace(/^'/, '')}</td></tr>` : ''}
                ${Full_Address || Street_Address ? `<tr><td style="font-weight: bold; padding: 4px; border-bottom: 1px solid #eee; vertical-align: top;">Address:</td><td style="padding: 4px; border-bottom: 1px solid #eee;">${Full_Address || Street_Address}</td></tr>` : ''}
              </tbody>
            </table>
          </div>
        `;

        new maplibregl.Popup()
          .setLngLat(coordinates)
          .setHTML(popupContent)
          .addTo(map);
      });

      // Change cursor on hover for schools
      map.on('mouseenter', LAYER_IDS.SCHOOLS_CIRCLE, () => {
        if (!drawModeRef.current && !radiusModeRef.current) {
          map.getCanvas().style.cursor = 'pointer';
        }
      });

      map.on('mouseleave', LAYER_IDS.SCHOOLS_CIRCLE, () => {
        if (!drawModeRef.current && !radiusModeRef.current) {
          map.getCanvas().style.cursor = '';
        }
      });


      // Enlarge draw vertex on hover to indicate closable/interactive points.
      // Fallback: when the event feature doesn't expose an id, compute the
      // nearest permanent vertex from the current draw points and target that
      // vertex so feature-state hover and cursor behave reliably.
      map.on('mousemove', LAYER_IDS.DRAW_CURRENT_POINTS, (event) => {
        if (!map) return;
        // Only show hover when drawing is active
        if (!drawModeRef.current) return;

        const feature = event.features && event.features[0];

        let id = null;
        let vertexIndex = null;

        if (feature) {
          id = feature.id !== undefined && feature.id !== null ? feature.id : feature.properties?.__id;
          vertexIndex = feature.properties?.vertexIndex;
        }

        // Nearest-vertex fallback when the event feature lacks an id
        if (!id) {
          const pts = (drawStateRef.current && Array.isArray(drawStateRef.current.points))
            ? drawStateRef.current.points
            : [];
          if (pts.length) {
            const mx = event.lngLat.lng;
            const my = event.lngLat.lat;
            let minDist = Infinity;
            let minIdx = -1;
            for (let i = 0; i < pts.length; i++) {
              const [px, py] = pts[i];
              const dx = px - mx;
              const dy = py - my;
              const d2 = dx * dx + dy * dy;
              if (d2 < minDist) {
                minDist = d2;
                minIdx = i;
              }
            }
            // threshold in degrees squared; adjust if needed (0.0009deg ≈ 100m)
            const threshold = 0.0009 * 0.0009;
            if (minIdx >= 0 && minDist <= threshold) {
              const base = drawSessionBaseRef.current || Number(String(Date.now()).slice(-6));
              id = base * 1000 + minIdx;
              vertexIndex = minIdx;
            }
          }
        }

        if (!id) return;

        try {
          const prev = drawHoverRef.current;
          if (prev && prev !== id) {
            try { map.setFeatureState({ source: MAP_SOURCES.DRAW_CURRENT, id: prev }, { hover: false }); } catch (e) { }
          }
          try { map.setFeatureState({ source: MAP_SOURCES.DRAW_CURRENT, id }, { hover: true }); } catch (e) { }
          drawHoverRef.current = id;

          // If this is the first clicked vertex and we have at least 3 points,
          // show pointer so the user can click to close the polygon.
          if (vertexIndex === 0 && drawStateRef.current.points.length >= 3) {
            map.getCanvas().style.cursor = 'pointer';
          } else {
            map.getCanvas().style.cursor = 'crosshair';
          }
        } catch (e) {
          // ignore non-fatal errors
        }
      });

      map.on('mouseleave', LAYER_IDS.DRAW_CURRENT_POINTS, () => {
        if (!map) return;
        const prev = drawHoverRef.current;
        if (prev) {
          try {
            map.setFeatureState({ source: MAP_SOURCES.DRAW_CURRENT, id: prev }, { hover: false });
          } catch (e) { }
          drawHoverRef.current = null;
        }
        if (drawModeRef.current) map.getCanvas().style.cursor = 'crosshair';
        else map.getCanvas().style.cursor = '';
      });

      console.log('[DEBUG] Setting isMapReady to true');
      setIsMapReady(true);
    });

    return () => {
      stopDrawInteraction();
      stopRadiusInteraction();
      map.remove();
      mapRef.current = null;
    };
  }, [stopDrawInteraction, stopRadiusInteraction, updateDrawPreview]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    const map = mapRef.current;
    try {
      map.getSource(MAP_SOURCES.NEIGHBORHOODS)?.setData(currentNeighborhoods);
    } catch (err) {
      console.warn('[MaptilerMap] failed to set neighborhoods source', err);
    }
  }, [currentNeighborhoods, isMapReady]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    const map = mapRef.current;
    try {
      map
        .getSource(MAP_SOURCES.NEIGHBORHOODS_SELECTED)
        ?.setData(selectedNeighborhoodCollection);
    } catch (err) {
      console.warn('[MaptilerMap] failed to set selected neighborhoods', err);
    }
  }, [selectedNeighborhoodCollection, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !apartmentsGeoJson) {
      return;
    }
    try {
      mapRef.current
        ?.getSource(MAP_SOURCES.APARTMENTS)
        ?.setData(apartmentsGeoJson);
    } catch (err) {
      console.warn('[MaptilerMap] failed to set apartments source', err);
    }

    // No development-only single-point test: use original GeoJSON data for markers.
  }, [apartmentsGeoJson, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !schoolsGeoJson) {
      return;
    }
    try {
      mapRef.current?.getSource(MAP_SOURCES.SCHOOLS)?.setData(schoolsGeoJson);
    } catch (err) {
      console.warn('[MaptilerMap] failed to set schools source', err);
    }
  }, [schoolsGeoJson, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !districtsGeoJson) {
      return;
    }
    mapRef.current?.getSource(MAP_SOURCES.DISTRICTS)?.setData(districtsGeoJson);
  }, [districtsGeoJson, isMapReady]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    const map = mapRef.current;

    if (radiusPolygonFeature) {
      map
        .getSource(MAP_SOURCES.RADIUS)
        ?.setData(turfFeatureCollection([radiusPolygonFeature]));
    } else {
      map.getSource(MAP_SOURCES.RADIUS)?.setData(EMPTY_COLLECTION);
    }
    // Ensure radius layers visibility matches whether we have a current radius polygon
    try {
      const visible = !!radiusPolygonFeature;
      if (map.getLayer(LAYER_IDS.RADIUS_FILL)) {
        map.setLayoutProperty(LAYER_IDS.RADIUS_FILL, 'visibility', visible ? 'visible' : 'none');
      }
      if (map.getLayer(LAYER_IDS.RADIUS_OUTLINE)) {
        map.setLayoutProperty(LAYER_IDS.RADIUS_OUTLINE, 'visibility', visible ? 'visible' : 'none');
      }
    } catch (e) {
      // ignore
    }
  }, [isMapReady, radiusPolygonFeature]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    const map = mapRef.current;
    map
      ?.getSource(MAP_SOURCES.CUSTOM_POLYGONS)
      ?.setData(turfFeatureCollection([...drawnPolygonsRef.current]));
  }, [drawnVersion, isMapReady]);

  // Handle escape key to cancel drawing
  useEffect(() => {
    if (!drawMode) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && drawMode) {
        stopDrawInteraction();
        if (onDrawUpdate) {
          const featureCollection = turfFeatureCollection([...drawnPolygonsRef.current]);
          onDrawUpdate({
            status: 'cancelled',
            polygons: featureCollection.features,
            apartmentsInPolygons: countApartmentsInShapes(
              apartmentsGeoJson,
              featureCollection.features.length ? [featureCollection] : []
            ),
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawMode, stopDrawInteraction, onDrawUpdate, apartmentsGeoJson]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    if (drawMode) {
      stopRadiusInteraction();
      startDrawInteraction();
    } else {
      stopDrawInteraction();
    }
  }, [drawMode, isMapReady, startDrawInteraction, stopDrawInteraction, stopRadiusInteraction]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    if (radiusMode) {
      stopDrawInteraction();
      startRadiusInteraction();
    } else {
      stopRadiusInteraction();
    }
  }, [radiusMode, isMapReady, startDrawInteraction, stopDrawInteraction, stopRadiusInteraction]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    applyLayerVisibility(
      [
        LAYER_IDS.NEIGHBORHOODS_FILL,
        LAYER_IDS.NEIGHBORHOODS_OUTLINE,
        LAYER_IDS.NEIGHBORHOODS_SELECTED_FILL,
        LAYER_IDS.NEIGHBORHOODS_SELECTED_OUTLINE,
        LAYER_IDS.NEIGHBORHOODS_HOVER_OUTLINE,
      ],
      showNeighborhoods
    );
  }, [applyLayerVisibility, isMapReady, showNeighborhoods]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    applyLayerVisibility([LAYER_IDS.APARTMENTS_CIRCLE], showApartments);
  }, [applyLayerVisibility, isMapReady, showApartments]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    applyLayerVisibility([LAYER_IDS.SCHOOLS_CIRCLE], showSchools);
  }, [applyLayerVisibility, isMapReady, showSchools]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    applyLayerVisibility(
      [LAYER_IDS.DISTRICTS_FILL, LAYER_IDS.DISTRICTS_OUTLINE, LAYER_IDS.DISTRICTS_TEXT],
      showDistricts
    );
  }, [applyLayerVisibility, isMapReady, showDistricts]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }

    // If any spatial selection exists (drawn polygons, selected neighborhoods,
    // or active radius), apply the filter so only markers inside the shapes
    // are visible. When no shapes are active, clear the filter so all markers
    // are shown. Also apply ZIP code filter when user selects ZIP codes.
    if (shapesForFiltering.length > 0) {
      setApartmentFilter(apartmentsInsideSet, schoolsInsideSet, true, selectedZipCodes);
    } else {
      setApartmentFilter(null, null, false, selectedZipCodes);
    }
  }, [
    apartmentsInsideSet,
    isMapReady,
    setApartmentFilter,
    shapesForFiltering.length,
    selectedZipCodes,
  ]);

  useEffect(() => {
    if (!apartmentsGeoJson || !onStatsUpdate) {
      return;
    }
    // Compute apartmentsVisible taking ZIP filters and spatial selection into account
    const totalApartments = apartmentsGeoJson.features.length;
    // Compute apartments in the currently selected city (if any)
    const lowerCity = (cityName || '').toLowerCase();
    const cityKeys = [
      'city_name',
      'city',
      'City',
      'market',
      'market_name',
      'cityName',
    ];
    // Prepare ZIP set and helper so apartmentsInCity can respect ZIP filters
    const selectedZipSet = new Set((selectedZipCodes || []).map((z) => String(z)));
    const zipKeys = ['zip', 'ZIP', 'zipcode', 'zip_code', 'postalCode', 'postal_code', 'ZIPCODE'];
    const featureHasSelectedZip = (feature) => {
      if (!selectedZipSet.size) return true; // no ZIP filter
      const props = feature.properties || {};
      for (let i = 0; i < zipKeys.length; i++) {
        const key = zipKeys[i];
        const v = props[key];
        if (v !== undefined && v !== null) {
          if (selectedZipSet.has(String(v))) return true;
        }
      }
      return false;
    };

    // Determine apartmentsInCity. If neighborhoods are selected, count
    // apartments inside the selected neighborhood polygons and also match
    // the selected ZIP codes (if any). Otherwise fall back to matching by
    // city name and ZIP codes.
    let apartmentsInCity = totalApartments;
    if (lowerCity) {
      if (selectedNeighborhoodCollection && selectedNeighborhoodCollection.features?.length) {
        // Count apartments inside selected neighborhoods, applying ZIP filter
        try {
          const idsInNeighborhoods = collectApartmentIdsInShapes(
            apartmentsGeoJson,
            [selectedNeighborhoodCollection]
          );
          apartmentsInCity = apartmentsGeoJson.features.reduce((acc, f) => {
            const id = f?.properties?.__id;
            if (!id) return acc;
            if (!idsInNeighborhoods.has(id)) return acc;
            if (!featureHasSelectedZip(f)) return acc;
            return acc + 1;
          }, 0);
        } catch (err) {
          // Fallback to city-name + ZIP matching
          apartmentsInCity = apartmentsGeoJson.features.reduce((acc, f) => {
            const props = f?.properties || {};
            for (let i = 0; i < cityKeys.length; i++) {
              const key = cityKeys[i];
              const v = props[key];
              if (v !== undefined && v !== null) {
                if (String(v).toLowerCase() === lowerCity && featureHasSelectedZip(f)) return acc + 1;
              }
            }
            return acc;
          }, 0);
        }
      } else {
        // No neighborhoods selected: count by city name, applying ZIP filter
        apartmentsInCity = apartmentsGeoJson.features.reduce((acc, f) => {
          const props = f?.properties || {};
          for (let i = 0; i < cityKeys.length; i++) {
            const key = cityKeys[i];
            const v = props[key];
            if (v !== undefined && v !== null) {
              if (String(v).toLowerCase() === lowerCity && featureHasSelectedZip(f)) return acc + 1;
            }
          }
          return acc;
        }, 0);
      }
    }

    let apartmentsVisible = 0;

    if (!showApartments) {
      apartmentsVisible = 0;
    } else if (onlySelectedTop && shapesForFiltering.length) {
      // Count apartments that are inside the spatial selection AND match ZIP filter (if any)
      apartmentsVisible = apartmentsGeoJson.features.reduce((acc, f) => {
        const id = f?.properties?.__id;
        if (!id) return acc;
        if (!apartmentsInsideSet.has(id)) return acc;
        if (!featureHasSelectedZip(f)) return acc;
        return acc + 1;
      }, 0);
    } else if ((selectedZipCodes || []).length > 0) {
      // No spatial restriction but ZIP filter present
      apartmentsVisible = apartmentsGeoJson.features.reduce((acc, f) => {
        if (featureHasSelectedZip(f)) return acc + 1;
        return acc;
      }, 0);
    } else {
      // No filters, show all
      apartmentsVisible = totalApartments;
    }

    onStatsUpdate({
      totalApartments,
      apartmentsInSelection: apartmentsInsideSet.size,
      apartmentsInCity,
      apartmentsVisible,
      propertyIds: Array.from(apartmentsInsideSet),
      drawnPolygonCount: drawnPolygonsRef.current.length,
      radiusActive: Boolean(radiusActive && radiusPolygonFeature),
      selectedNeighborhoodCount: selectedNeighborhoodCollection.features.length,
    });
  }, [
    apartmentsGeoJson,
    apartmentsInsideSet.size,
    onStatsUpdate,
    onlySelectedTop,
    radiusActive,
    radiusPolygonFeature,
    selectedNeighborhoodCollection.features.length,
    shapesForFiltering.length,
    showApartments,
    selectedZipCodes,
    cityName,
  ]);

  useEffect(() => {
    if (!apartmentsGeoJson || !onRadiusUpdate) {
      return;
    }
    if (!radiusCenter) {
      onRadiusUpdate({
        status: 'cleared',
        center: null,
        radiusMiles: radiusInMiles,
        apartmentsInRadius: 0,
        applied: false,
      });
      return;
    }
    const circle = radiusPolygonFeature;
    const count = countApartmentsInShapes(
      apartmentsGeoJson,
      circle ? [circle] : []
    );
    onRadiusUpdate({
      status: radiusActive ? 'applied' : 'preview',
      center: radiusCenter,
      radiusMiles: radiusInMiles,
      apartmentsInRadius: count,
      applied: radiusActive,
    });
  }, [
    apartmentsGeoJson,
    onRadiusUpdate,
    radiusActive,
    radiusCenter,
    radiusInMiles,
    radiusPolygonFeature,
  ]);

  useEffect(() => {
    if (!isMapReady || clearDrawTriggerRef.current === clearDrawTrigger) {
      return;
    }
    clearDrawTriggerRef.current = clearDrawTrigger;
    drawnPolygonsRef.current = [];
    setDrawnVersion((version) => version + 1);
    mapRef.current
      ?.getSource(MAP_SOURCES.CUSTOM_POLYGONS)
      ?.setData(EMPTY_COLLECTION);
    updateDrawPreview();
    if (onDrawUpdate) {
      onDrawUpdate({
        status: 'cleared',
        polygons: [],
        apartmentsInPolygons: 0,
      });
    }
  }, [clearDrawTrigger, isMapReady, onDrawUpdate, updateDrawPreview]);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !cityName) {
      return;
    }
    const map = mapRef.current;

    if (currentNeighborhoods.features?.length) {
      try {
        const bounds = turfBbox(currentNeighborhoods);
        if (Number.isFinite(bounds[0])) {
          map.fitBounds(
            [
              [bounds[0], bounds[1]],
              [bounds[2], bounds[3]],
            ],
            {
              padding: { top: 48, bottom: 48, left: 48, right: 48 },
              maxZoom: 12,
            }
          );
          return;
        }
      } catch (error) {
        console.warn('Failed to fit bounds for neighborhoods', error);
      }
    }

    const viewport =
      CITY_VIEWPORTS[cityName] ||
      CITY_VIEWPORTS[cityName?.toUpperCase()] ||
      INITIAL_VIEWPORT;
    map.flyTo({
      center: viewport.center,
      zoom: viewport.zoom,
      essential: true,
    });
  }, [cityName, currentNeighborhoods, isMapReady]);

  useEffect(() => {
    if (!isOpen || !isMapReady || !mapRef.current) {
      return;
    }
    const map = mapRef.current;
    const timeout = setTimeout(() => map.resize(), 180);
    return () => clearTimeout(timeout);
  }, [isOpen, isMapReady]);

  return (
    <div className={`maptiler-map ${drawMode ? 'maptiler-map--drawing' : ''} ${className}`}>
      <div ref={mapContainerRef} className='maptiler-map__canvas' />
      <div className='maptiler-map__badge'>
        <span className='maptiler-map__badge-dot' />
        {cityName || 'Select a market to explore'}
      </div>
      {hoveredNeighborhoodName && (
        <div className='maptiler-map__neighborhood-tooltip'>
          {hoveredNeighborhoodName}
        </div>
      )}
      {drawMode && (
        <div className='maptiler-map__instructions'>
          {drawStateRef.current.points.length === 0 ? (
            <>Click on the map to start drawing • Press <kbd>Esc</kbd> to cancel</>
          ) : drawStateRef.current.points.length < 3 ? (
            <>Continue clicking to draw area • Press <kbd>Esc</kbd> to cancel</>
          ) : (
            <>Click first point or double-click anywhere to complete • Press <kbd>Esc</kbd> to cancel</>
          )}
        </div>
      )}
    </div>
  );
};

export default MaptilerMap;
